import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  deleteRule,
  listRules,
  pauseRule,
  resumeRule,
} from '@/api/endpoints';
import type { SyncRule } from '@/api/types';
import { PageHeader } from '@/components/PageHeader';
import { ConfirmTypedDelete } from '@/components/ConfirmTypedDelete';

export function RulesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const rules = useQuery<SyncRule[]>({ queryKey: ['rules'], queryFn: listRules });
  const [pendingDelete, setPendingDelete] = useState<SyncRule | null>(null);

  const pauseToggle = useMutation({
    mutationFn: (rule: SyncRule) => (rule.paused ? resumeRule(rule.id) : pauseRule(rule.id)),
    onSuccess: (rule) => {
      queryClient.invalidateQueries({ queryKey: ['rules'] });
      enqueueSnackbar(`Rule "${rule.name}" ${rule.paused ? 'paused' : 'resumed'}.`, {
        variant: 'success',
      });
    },
    onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rules'] });
      enqueueSnackbar('Rule deleted.', { variant: 'success' });
      setPendingDelete(null);
    },
    onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
  });

  return (
    <Container maxWidth="lg" disableGutters>
      <PageHeader
        title="Rules"
        subtitle="Each rule mirrors busy events one-way from a source calendar to a target."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/rules/new')}
          >
            New rule
          </Button>
        }
      />

      {rules.isLoading && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={28} />
        </Stack>
      )}

      {rules.isSuccess && rules.data.length === 0 && (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" gutterBottom>
              No rules yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create your first rule to start mirroring.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/rules/new')}>
              Create rule
            </Button>
          </CardContent>
        </Card>
      )}

      {rules.isSuccess && rules.data.length > 0 && (
        <TableContainer component={Card} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>State</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.data.map((rule) => (
                <TableRow key={rule.id} hover>
                  <TableCell>
                    <RouterLink to={`/rules/${rule.id}`} style={{ color: 'inherit' }}>
                      {rule.name}
                    </RouterLink>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        “{rule.replacement_title}” · min {rule.min_duration_minutes}m
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        rule.detection_mode === 'webhook'
                          ? 'Webhook'
                          : `Poll · ${rule.poll_interval_s}s`
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={rule.paused ? 'default' : 'success'}
                      label={rule.paused ? 'Paused' : 'Active'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label={rule.paused ? 'Resume rule' : 'Pause rule'}
                      onClick={() => pauseToggle.mutate(rule)}
                      disabled={pauseToggle.isPending}
                    >
                      {rule.paused ? <PlayArrowIcon /> : <PauseIcon />}
                    </IconButton>
                    <IconButton
                      aria-label="Delete rule"
                      onClick={() => setPendingDelete(rule)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmTypedDelete
        open={pendingDelete !== null}
        title="Delete rule"
        body={
          pendingDelete
            ? `This will delete "${pendingDelete.name}" and stop mirroring. Mirrored events already in the target calendar are not touched.`
            : undefined
        }
        confirmText={pendingDelete?.name ?? ''}
        pending={remove.isPending}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </Container>
  );
}
